import { MODULE_KEYS } from '@ctcells/core'
import { moduleLabel, moduleStateLabel, t, type ModuleState } from '@ctcells/i18n'

import type { BatchStripBatch, BatchStripSegment } from './batchStripTypes.ts'

/**
 * 작업 줄 2행 **공정 칸**의 재료 — 순수 함수만. 그리는 일은 `StripStages.tsx` 가 한다.
 *
 * 디자인팀 시안 2026-09-30 §2 「공정 진행」: 칸 셋(원심 → 어셈블 → 염색)이 같은 뼈대이고 상태는 클래스 하나
 * (`stage--done | run | wait | pause | stop`)로만 갈린다(`atoms/stage.css` · 모니터링 단계 카드와 같은 틀).
 *
 * 🔴 **재료는 모델이 준 구간(`BatchStripSegment`)과 작업 상태뿐이다.** 여기서 서버에 없는 값을 만들지 않는다 —
 *    구간의 `state`(끝남 · 도는 중 · 아직)와 `progress` · `step` · `total` 은 `stripSegments()` 한 규칙에서 나온 것이고,
 *    「도는 중」을 일시정지 · 중단으로 가르는 것은 서버가 준 작업 상태(`Batch.status`)다.
 * 🔴 **모르면 말하지 않는다.** 구간이 하나도 없으면(구독 전) 이름만 선 칸 셋이고, 어느 칸이 지금인지 모르면 배지가 없다.
 */

/** 공정 칸의 상태 다섯 — 시안 `stage--done | run | wait | pause | stop` 그대로(모니터링 `StageState` 와 같은 어휘) */
export type StripStageState = 'done' | 'run' | 'wait' | 'pause' | 'stop'

export interface StripStagePill {
  /** 배지 글자 — 진행 중이면 서버가 준 지금 스텝 문구, 아니면 상태 이름 */
  label: string
  /** `StatusBadge` 의 원값 — 모양(점 · ▲ · !)을 고르는 데만 쓴다 */
  status: string
  /** 툴팁 — 배지가 잘릴 때 원문 */
  title?: string
}

export interface StripStageCell {
  key: string
  /** 모듈 표기(한글) */
  label: string
  /** 칸의 상태. **모르면 없다**(작업 없음 · 지금 자리를 모름) — 배지가 서지 않는다 */
  state?: StripStageState
  /**
   * **작업은 돌고 있는데 이 칸이 어디쯤인지 모른다**(상태 없음 + 도는 작업). 막대가 빗금(「집계 중」 모양)으로 선다 — 배지는 없다.
   * 🔴 이때 「대기」라고 하지 않는다(2026-10-06 쵸단 실측 — 6축 이송 구간처럼 어느 모듈도 그 작업의 실행을 안 쥔 동안,
   *    지난 원심분리기까지 「대기」였다). 모르는 것은 «모름»이다.
   */
  active?: true
  /** 채움 0~1(모델의 `progress`). 총수를 모르면 0 */
  progress: number
  step?: number
  total?: number
  estimated?: boolean
  /** 이름 툴팁(구간 `title` — 「원심분리기 96/96 · 지난 작업 기준」) */
  title?: string
  pill?: StripStagePill
  /**
   * **트레이 격리 · 낙하 표지**(`BatchStripBatch.trayHolds` · 서버 `TrayTrack.last_module_id` 칸) — 칸 상태 배지 옆에 하나 더 선다.
   * 🔴 칸 상태(`state`)를 바꾸지 않는다 — 트레이의 일이지 그 모듈 공정의 진행이 아니다. 없으면 키가 없다.
   */
  hold?: StripStagePill
}

/** 상태 배지의 원값 — 모양만 빌린다. 글자는 `pillOf` 가 따로 준다 */
const PILL_STATUS: Record<StripStageState, string> = {
  done: 'completed',
  run: 'running',
  wait: 'IDLE',
  pause: 'paused',
  stop: 'STOPPED',
}

/**
 * 구간 상태 + 작업 상태 → 칸 상태.
 *   끝남 → done · 아직 → wait · 도는 중 → 작업이 일시정지면 pause, 오류·중단이면 stop, 끝났으면 done, 아니면 run.
 * 🔴 `state` 가 없으면(지금 자리를 모름) `undefined` — 「대기」라고도 「진행 중」이라고도 하지 않는다.
 */
export function stripStageState(
  segment: Pick<BatchStripSegment, 'state'>,
  status: BatchStripBatch['status'],
): StripStageState | undefined {
  if (segment.state === 'done') return 'done'
  if (segment.state === 'pending') return 'wait'
  if (segment.state !== 'current') return undefined
  if (status === 'paused') return 'pause'
  if (status === 'error' || status === 'aborted') return 'stop'
  if (status === 'completed') return 'done'
  return 'run'
}

/**
 * 배지 — 진행 중이면 **서버가 준 지금 스텝 문구**(`currentStep` · 시안 「이동 중」 자리)가 글자다. 문구가 없으면 「실행 중」.
 * 일시정지 · 중단은 상태 이름이 글자이고 문구는 툴팁에만 남는다(시안 `ST_LABEL` — 멈춘 칸에 동작을 적지 않는다).
 * 🔴 문구는 원문 그대로다(`stripStepText` 가 만든 것) — 여기서 번역하거나 줄이지 않는다. 툴팁이 전문을 말한다.
 */
function pillOf(state: StripStageState, currentStep: string | undefined): StripStagePill {
  const status = PILL_STATUS[state]
  if (currentStep === undefined || state === 'done' || state === 'wait')
    return { label: t(`strip.stage.${state}`), status }
  return {
    label: state === 'run' ? currentStep : t(`strip.stage.${state}`),
    status,
    title: currentStep,
  }
}

/**
 * 지금 스텝 문구를 **어느 칸이 지고 있는가.** 도는 · 멈춘 칸(run · pause · stop)이 있으면 그 배지(글자 또는 툴팁)가 말한다.
 * 🔴 없으면(구독이 모듈 종류를 안 실었거나 구간이 아직 없다) 1행이 종전대로 문구를 적는다 — 아는 사실을 숨기지 않는다.
 */
export function stripStepCarried(cells: readonly StripStageCell[]): boolean {
  return cells.some(
    (cell) => cell.state === 'run' || cell.state === 'pause' || cell.state === 'stop',
  )
}

/**
 * 작업이 «돌고 있는» 상태인가(시작했고 아직 종착 전) — 이때 지금 자리를 모르는 칸은 「대기」가 아니라 «모름»이다.
 * 🔴 서버가 준 `Batch.status` 와 `started_at` 만 본다(`started` 는 `toStripBatch` 가 `started_at` 으로 단다).
 */
function inFlight(batch: BatchStripBatch): boolean {
  return batch.started === true && (batch.status === 'running' || batch.status === 'paused')
}

/**
 * 칸 셋. 구간이 없으면(구독 전 · 작업 없음) **이름만 선 칸 셋**이다 — 공정 순서(`MODULE_KEYS`)는 제품 정의라 늘 셋이다.
 * ⚠ 시작 전 작업이면 칸 셋이 「대기」다 — 아직 아무 모듈도 안 돌았다는 사실 그대로다(총수는 모른다).
 * 🔴 **돌고 있는 작업인데 구간이 없으면 «모름»이다**(배지 없음 · 빗금 막대 — `active`). 종전에는 이때도 「대기」였고, 6축 이송
 *    구간(어느 모듈도 그 작업의 실행을 안 쥔 동안)에 화면을 연 사람에게 끝난 원심분리기까지 「대기」로 보였다(2026-10-06 쵸단 실측).
 */
export function stripStageCells(batch: BatchStripBatch | null | undefined): StripStageCell[] {
  const segments = batch?.segments
  const active = batch != null && inFlight(batch)
  if (segments === undefined || segments.length === 0) {
    return MODULE_KEYS.map((key) => {
      const hold = holdPill(batch, key)
      return {
        key,
        label: moduleLabel(key),
        progress: 0,
        ...(batch == null
          ? {}
          : active
            ? { active: true as const }
            : { state: 'wait' as const, pill: pillOf('wait', undefined) }),
        ...(hold === undefined ? {} : { hold }),
      }
    })
  }
  return segments.map((segment) => {
    const state = batch == null ? undefined : flowCorrected(segment, batch)
    const hold = holdPill(batch, segment.key)
    return {
      ...(hold === undefined ? {} : { hold }),
      key: segment.key,
      label: segment.label,
      progress: segment.progress,
      ...(state === undefined ? {} : { state, pill: pillOf(state, batch?.currentStep) }),
      ...(state === undefined && active ? { active: true as const } : {}),
      ...(segment.step === undefined ? {} : { step: segment.step }),
      ...(segment.total === undefined ? {} : { total: segment.total }),
      ...(segment.estimated === true ? { estimated: true } : {}),
      ...(segment.title === undefined ? {} : { title: segment.title }),
    }
  })
}

/**
 * 그 칸 모듈에 걸린 트레이 격리 · 낙하 표지 — 서버가 «떠나기 전 모듈»로 말한 칸에만(`stripTrayHolds`). 둘 이상이면 글자는 첫 것 ·
 * 툴팁이 전부를 말한다(트레이 이름 · 격리 행방 원문). 🔴 위험 꼴(`fail` 원값 — 팔각형 !)이고 글자가 같은 말을 한다(색만으로 말하지 않는다).
 */
function holdPill(
  batch: BatchStripBatch | null | undefined,
  key: string,
): StripStagePill | undefined {
  const holds = (batch?.trayHolds ?? []).filter((hold) => hold.key === key)
  const first = holds[0]
  if (first === undefined) return undefined
  return {
    label: t(`strip.stage.hold.${first.where}`),
    status: 'fail',
    title: holds
      .map((hold) =>
        [t(`strip.stage.hold.${hold.where}`), hold.tray, hold.whereabouts]
          .filter((part) => part !== undefined && part !== '')
          .join(' · '),
      )
      .join(' / '),
  }
}

function flowCorrected(
  segment: Pick<BatchStripSegment, 'state' | 'key'>,
  batch: BatchStripBatch,
): StripStageState | undefined {
  if (segment.state === 'current') {
    if (batch.loadWait?.key === segment.key) return 'wait'
    if (stageLeft(batch)) return 'done'
    if (batch.transferring === true) return undefined
  }
  return stripStageState(segment, batch.status)
}

/** 영역 칸 — 장비 칸 앞의 «준비»와 끝의 «완료». 막대 · 셈이 없다(그 수를 서버가 안 준다) */
export interface StripZoneCell {
  key: string
  kind: 'prep' | 'done'
  /** 눈에 보이는 이름(「준비」 · 「완료」) — 다음 칸이 장비 이름을 말한다 */
  label: string
  /** 접근 이름 · 툴팁(「원심분리기 대기」 · 「완료」) — 홀로 읽혀도 뜻이 선다 */
  name: string
  /** 모르면 없다 — 배지가 서지 않는다 */
  state?: StripStageState
  pill?: StripStagePill
  /** 준비 영역이면 그 뒤 장비 칸의 차례(0~2). 완료 영역은 없다 */
  before?: number
}

/** 칸이 «지금»인가 — 도는 · 멈춘 · 중단된 칸(시안의 강조 칸) */
export function stripCellIsNow(cell: Pick<StripStageCell, 'state'>): boolean {
  return cell.state === 'run' || cell.state === 'pause' || cell.state === 'stop'
}

/**
 * **준비 · 완료 영역 넷**(eric 2026-10-06) — `[준비] › 원심분리기 › [준비] › 어셈블러 › [준비] › 염색기 › [완료]` 의 영역 칸.
 * (처음 이름은 「원심 준비」 …였다가 「대기」를 거쳐 같은 날 「준비」로 돌아왔다 — 「대기」가 칸 상태 글자와 겹쳤다. 번호 ①②③ 은 뺐다.)
 *
 * ⚠ 중단 · 실패로 끝난 작업(`aborted` · `error` 종착)의 완료 영역은 상태가 없다 — 거기에 닿지 않았다.
 */
export function stripFlowZones(
  batch: BatchStripBatch | null | undefined,
  cells: readonly StripStageCell[],
): StripZoneCell[] {
  const prepKeys = MODULE_KEYS.map((key, before) => ({ key, before }))
  const base = (key: (typeof MODULE_KEYS)[number], before: number): StripZoneCell => ({
    key: `prep-${key}`,
    kind: 'prep',
    label: t('strip.zone.prep'),
    name: t('strip.zone.prepName', { module: moduleLabel(key) }),
    before,
  })
  const doneLabel = t('strip.zone.done')
  const done: StripZoneCell = { key: 'done', kind: 'done', label: doneLabel, name: doneLabel }
  if (batch == null) return [...prepKeys.map(({ key, before }) => base(key, before)), done]

  const status = batch.status
  const nowState = (): StripStageState =>
    status === 'paused' ? 'pause' : status === 'error' || status === 'aborted' ? 'stop' : 'run'
  /*
   * 🔴 **준비 칸의 «대기» 상태에는 배지가 없다**(팀장 결정 2026-10-06 — 이름이 「대기」이던 때 「대기 [대기]」로 겹쳐 뺐고, 이름이
   *    「준비」로 돌아온 뒤에도 좁은 칸에 배지를 늘리지 않는다). 상태(`stage--wait`)는 그대로 싣고 글자만 뺀다.
   *    «지금»(준비 중 · 적재 대기 · 6축 이송 중) · 완료 · 멈춤 배지는 그대로 선다. 완료 칸의 「대기」는 둔다.
   */
  const withState = (zone: StripZoneCell, state: StripStageState | undefined): StripZoneCell =>
    state === undefined
      ? zone
      : zone.kind === 'prep' && state === 'wait'
        ? { ...zone, state }
        : { ...zone, state, pill: zonePill(zone, state, batch) }

  const loadWaitKey = batch.loadWait?.key
  const loadWaitAt = loadWaitKey === undefined ? -1 : cells.findIndex((c) => c.key === loadWaitKey)
  const prepNow = inFlight(batch) ? loadWaitAt : -1
  /* 지금 서 있는 장비 칸(도는 칸) — 준비 영역이 지금이 아니면 이 칸 앞의 대기는 지났다 */
  const moduleNow = cells.findIndex((cell) => stripCellIsNow(cell))
  const reached = prepNow >= 0 ? prepNow : moduleNow >= 0 ? moduleNow : lastDone(cells)

  const preps = prepKeys.map(({ key, before }) => {
    const zone = base(key, before)
    if (status === 'completed') return withState(zone, 'done')
    if (status === 'created') return withState(zone, 'wait')
    if (
      inFlight(batch) &&
      batch.transferring === true &&
      batch.transferRoutes?.some((route) => route.destination === key)
    )
      return withState(zone, nowState())
    if (before === prepNow) return withState(zone, nowState())
    if (reached < 0) return zone
    /* 대기가 지금이면 그 앞만 지났다. 장비 칸이 지금(또는 끝까지 왔다)이면 그 칸의 대기까지 지났다 */
    const passed = prepNow >= 0 ? before < prepNow : before <= reached
    return withState(zone, passed ? 'done' : 'wait')
  })
  const doneState: StripStageState | undefined =
    status === 'completed' ? 'done' : status === 'created' || inFlight(batch) ? 'wait' : undefined
  return [...preps, withState(done, doneState)]
}

function lastDone(cells: readonly StripStageCell[]): number {
  let at = -1
  cells.forEach((cell, index) => {
    if (cell.state === 'done') at = index
  })
  return Math.min(at, cells.length - 1)
}

/** 영역 배지 — 지금이면 «무엇을 하나»(적재 대기는 서버 상태 원값의 한글 · 6축이면 「6축 이송 중」 · 아니면 「준비 중」), 멈춤 · 중단은 상태 이름 */
function zonePill(
  zone: StripZoneCell,
  state: StripStageState,
  batch: BatchStripBatch,
): StripStagePill {
  const status = PILL_STATUS[state]
  if (state !== 'run') return { label: t(`strip.stage.${state}`), status }
  const loadWait = batch.loadWait
  if (zone.kind === 'prep' && loadWait !== undefined && zone.key === `prep-${loadWait.key}`)
    return {
      label: moduleStateLabel(loadWait.state as ModuleState),
      status,
      title: loadWait.state,
    }
  if (zone.kind === 'prep' && batch.transferring === true) {
    const routes =
      batch.transferRoutes?.filter((route) => zone.key === `prep-${route.destination}`) ?? []
    const title = routes
      .map((route) =>
        [route.source, route.destination]
          .filter((key) => key !== undefined)
          .map(moduleLabel)
          .join(' → '),
      )
      .join(' · ')
    return { label: t('strip.zone.transfer'), status, ...(title === '' ? {} : { title }) }
  }
  return { label: t('strip.zone.preparing'), status }
}

function stageLeft(batch: BatchStripBatch): boolean {
  return batch.stageSettled === true
}

/**
 * 축약형(시안 `brief()`)이 보이는 **현재 공정** — 끝나지 않은 첫 칸, 전부 끝났으면 마지막 칸.
 * 🔴 지금 자리를 모르면(상태 없음) 첫 칸이다 — 「어디쯤」을 추론하지 않고 차례의 처음을 보인다.
 */
export function stripBriefIndex(cells: readonly StripStageCell[]): number {
  const at = cells.findIndex((cell) => cell.state !== 'done')
  return at < 0 ? Math.max(0, cells.length - 1) : at
}

/**
 * 셈이 서는가 — 총수를 알고 0 보다 크다. 현재 스텝을 아직 못 들었으면 `0` 이다(모델의 `countText` 와 같은 규칙 —
 * 총수를 아는 모듈이 아직 한 스텝도 안 했다는 사실이라 거짓이 아니다).
 */
export function stripStageKnown(
  cell: Pick<StripStageCell, 'step' | 'total'>,
): cell is StripStageCell & { total: number } {
  return cell.total !== undefined && cell.total > 0
}

/**
 * 「00 사전 점검」 칸의 상태(eric 2026-10-29 「01 원심분리기 왼쪽에.. 00 사전 점검 이라는 칸 넣어 주고... 시작 누르면.. 해당 부분이
 * 하이라이트 진행 중이라는 색상 표시되면서... 사전 점검 창이 뜨게」).
 *
 * | 값         | 글자        | 강조 |
 * | ---------- | ----------- | ---- |
 * | `idle`     | 「–」        | 없음 |
 * | `checking` | 「점검 중」  | 있음 |
 * | `passed`   | 「통과 ✓」   | 있음 |
 * | `blocked`  | 「막힘 ▲」   | 있음 |
 * | `done`     | 「완료 ✓」   | 없음 |
 */
export type StripPreflightPhase = 'idle' | 'checking' | 'passed' | 'blocked' | 'done'

/**
 * 칸 상태를 정한다 — 재료는 서버 사실(`started` = `started_at` 유무 · 점검 결과 `passed`)과 화면이 연 창(UI 상태)뿐이다.
 * 🔴 작업이 시작됐는지는 서버 칸(`started_at`)이 말한다 — 시작 명령이 받아들여졌다(202)는 것으로 「완료」를 앞질러 그리지 않는다.
 * 🔴 통과 · 막힘은 서버 점검 결과의 `passed` 그대로다. 시작 명령이 나가는 중이면 그 앞 점검이 `passed` 였다는 뜻이다
 *    (`useStartGate` 가 통과한 결과에서만 시작을 보낸다).
 * 🔴 창을 닫고 시작하지 않으면 「–」로 돌아간다 — 지난 점검 결과를 쥐고 있지 않는다(그 사이 사정이 바뀐다).
 */
export function stripPreflightPhase({
  started,
  checking,
  result,
  starting,
}: {
  /** 서버 `started_at` 이 있는 작업인가 */
  started: boolean
  /** 점검 앞 준비(시약 용량 확인 창 · 시뮬 준비) 또는 사전 점검 요청이 도는 중 */
  checking: boolean
  /** 지금 열린 사전 점검 창의 결과(`passed`). 창이 없으면 `undefined` */
  result: { passed: boolean } | undefined
  /** 시작 명령이 날아가 있다 */
  starting: boolean
}): StripPreflightPhase {
  if (started) return 'done'
  if (result !== undefined) return result.passed ? 'passed' : 'blocked'
  if (starting) return 'passed'
  if (checking) return 'checking'
  return 'idle'
}
